import { State, appendOrRemoveValueWith } from '@btime/state-monad'
import { compose, concat, difference, isEmpty, prop, propOr, uniq } from 'ramda'
import React from 'react'

const Context = React.createContext()
const { Provider } = Context
export const useMultiSelectState = () => React.useContext(Context)
export const useIsUsingMultiSelect = () => propOr(false, 'active', useMultiSelectState())

const MultiSelectHandler = ({ children }) => {
  const [isSelecting, setIsSelecting] = React.useState(false)
  const [selecteds, setSelecteds] = React.useState([])

  const isAllSelected = React.useCallback(
    (data, rowKey = 'id') => {
      const ids = data.map(prop(rowKey))
      return compose(isEmpty, difference(ids))(selecteds)
    },
    [selecteds]
  )

  const getSelectedData = React.useCallback(
    (data = [], rowKey = 'id') => data.filter(item => selecteds.includes(item[rowKey])),
    [selecteds]
  )

  const setUniqSelecteds = React.useCallback(newState => setSelecteds(uniq(newState)), [setSelecteds])
  const mergeSelecteds = React.useCallback(
    newState => setUniqSelecteds(concat(selecteds, newState)),
    [setUniqSelecteds, selecteds]
  )

  const removeSelecteds = React.useCallback(
    ids => setUniqSelecteds(selecteds.filter(id => !ids.includes(id))),
    [setUniqSelecteds, selecteds]
  )

  const handleSelect = React.useCallback(
    id => () => {
      setSelecteds(
        State.of(selecteds)
          .map(appendOrRemoveValueWith(selectedId => selectedId === id, id))
          .fold()
      )
    },
    [selecteds, setSelecteds]
  )

  const clearSelection = React.useCallback(() => {
    setSelecteds([])
  }, [setSelecteds])

  const selectAll = React.useCallback(
    (data, rowKey = 'id') => {
      const ids = data.map(prop(rowKey))
      const isActive = isAllSelected(data, rowKey)
      return !isActive ? mergeSelecteds(ids) : removeSelecteds(ids)
    },
    [isAllSelected]
  )

  const toggleSelectionMode = () => {
    clearSelection()
    setIsSelecting(!isSelecting)
  }

  const state = {
    selecteds,
    isAllSelected,
    active: true,
    clearSelection,
    removeSelecteds,
    selectAll,
    handleSelect,
    getSelectedData,
    mergeSelecteds,
    isSelecting,
    toggleSelectionMode
  }

  return <Provider value={state}>{children}</Provider>
}

export default MultiSelectHandler
